import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Edit, Trash2, Search, Star } from 'lucide-react';
import { productAPI } from '../../services/api';
import toast from 'react-hot-toast';

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [deleting, setDeleting] = useState(null);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 10 };
      if (search) params.search = search;
      const { data } = await productAPI.getAll(params);
      setProducts(data.products);
      setPages(data.pages);
      setTotal(data.total);
    } catch { toast.error('Failed to load products'); }
    finally { setLoading(false); }
  }, [page, search]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    setDeleting(id);
    try {
      await productAPI.delete(id);
      toast.success('Product deleted');
      fetchProducts();
    } catch { toast.error('Failed to delete product'); }
    finally { setDeleting(null); }
  };

  return (
    <div style={{ padding: '32px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '36px', fontWeight: 400 }}>Products</h1>
          <p style={{ color: 'var(--white-dim)', fontSize: '13px', marginTop: '4px' }}>{total} total products</p>
        </div>
        <Link to="/admin/products/new" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'var(--gold)', color: 'var(--black)', padding: '12px 24px', fontSize: '11px', fontWeight: 700, letterSpacing: '0.15em', transition: 'background 0.3s' }}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--gold-light)'}
          onMouseLeave={e => e.currentTarget.style.background = 'var(--gold)'}
        ><Plus size={14} /> ADD PRODUCT</Link>
      </div>

      {/* Search */}
      <div style={{ position: 'relative', maxWidth: '400px', marginBottom: '24px' }}>
        <Search size={14} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: 'var(--white-dim)' }} />
        <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search products..."
          style={{ width: '100%', padding: '10px 14px 10px 40px', background: 'var(--dark-2)', border: '1px solid var(--border)', color: 'var(--white)', fontSize: '13px', outline: 'none' }} />
      </div>

      {/* Table */}
      <div style={{ background: 'var(--dark-2)', border: '1px solid var(--border)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {['PRODUCT', 'BRAND', 'PRICE', 'STOCK', 'RATING', 'FEATURED', 'ACTIONS'].map(h => (
                  <th key={h} style={{ fontSize: '10px', letterSpacing: '0.2em', color: 'var(--white-dim)', textAlign: 'left', padding: '14px 16px' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? [...Array(5)].map((_, i) => (
                <tr key={i}><td colSpan={7} style={{ padding: '16px' }}><div className="skeleton" style={{ height: '20px' }} /></td></tr>
              )) : products.map(p => (
                <tr key={p._id} style={{ borderBottom: '1px solid rgba(201,168,76,0.05)', transition: 'background 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(201,168,76,0.03)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ width: '48px', height: '48px', background: 'var(--dark-3)', flexShrink: 0, overflow: 'hidden' }}>
                        {p.images?.[0] && <img src={p.images[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                      </div>
                      <div>
                        <p style={{ fontSize: '13px', fontWeight: 500, marginBottom: '2px' }}>{p.name}</p>
                        <p style={{ fontSize: '11px', color: 'var(--white-dim)', textTransform: 'capitalize' }}>{p.category} · {p.gender}</p>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--white-dim)' }}>{p.brand}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ fontFamily: 'var(--font-display)', fontSize: '18px' }}>${p.salePrice || p.price}</span>
                    {p.salePrice && <span style={{ fontSize: '11px', color: 'var(--white-faint)', textDecoration: 'line-through', marginLeft: '6px' }}>${p.price}</span>}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ fontSize: '13px', color: p.countInStock === 0 ? '#ef4444' : p.countInStock <= 5 ? '#f59e0b' : 'var(--white)' }}>
                      {p.countInStock}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Star size={12} fill="var(--gold)" color="var(--gold)" />
                      <span style={{ fontSize: '12px' }}>{p.rating.toFixed(1)}</span>
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ fontSize: '11px', padding: '3px 8px', background: p.isFeatured ? 'rgba(201,168,76,0.15)' : 'transparent', border: `1px solid ${p.isFeatured ? 'var(--gold)' : 'var(--border)'}`, color: p.isFeatured ? 'var(--gold)' : 'var(--white-faint)' }}>
                      {p.isFeatured ? 'FEATURED' : 'STANDARD'}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <Link to={`/admin/products/edit/${p._id}`} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: 'var(--gold)', letterSpacing: '0.1em' }}>
                        <Edit size={12} /> EDIT
                      </Link>
                      <button onClick={() => handleDelete(p._id, p.name)} disabled={deleting === p._id}
                        style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#ef4444', background: 'none', letterSpacing: '0.1em', opacity: deleting === p._id ? 0.5 : 1 }}>
                        <Trash2 size={12} /> DELETE
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {products.length === 0 && !loading && (
          <div style={{ textAlign: 'center', padding: '48px', color: 'var(--white-dim)' }}>
            <p style={{ fontFamily: 'var(--font-display)', fontSize: '22px', marginBottom: '8px' }}>No products found</p>
            <Link to="/admin/products/new" style={{ color: 'var(--gold)', fontSize: '13px' }}>Add your first product →</Link>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '24px' }}>
          {[...Array(pages)].map((_, i) => (
            <button key={i} onClick={() => setPage(i + 1)}
              style={{ width: '36px', height: '36px', border: `1px solid ${page === i + 1 ? 'var(--gold)' : 'var(--border)'}`, background: page === i + 1 ? 'var(--gold)' : 'transparent', color: page === i + 1 ? 'var(--black)' : 'var(--white)', fontSize: '12px', cursor: 'pointer' }}>
              {i + 1}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminProducts;
